// Settings -> Configuration -> Sessions (documentation/entra.md "Session lifetimes"): how long a
// sign-in lasts. One global policy for every person: the Management Client session (minutes) and
// the Dispatcher session (hours), both counted from sign-in and never extended by activity. A
// change applies at once to every signed-in person: a shorter lifetime ends sessions older than
// it, a longer one extends sessions that are still live (never expired or signed-out ones).
import { useCallback, useEffect, useState } from 'react';
import { ConfigurationError, boundedInteger, getSessionPolicy, saveSessionPolicy, type SessionPolicyView } from '../../../services/configuration';
import { Card, Rows } from './ConfigurationViews';
import { changeSummary, hoursText } from './sessionsModel';

const when = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');

export function SessionsView({ view, busy, onSave }: {
  view: SessionPolicyView;
  busy: boolean;
  onSave: (adminMinutes: number, dispatcherHours: number) => void;
}) {
  const b = view.bounds;
  const [admin, setAdmin] = useState(String(view.admin_session_minutes));
  const [dispatcher, setDispatcher] = useState(String(view.dispatcher_session_hours));
  useEffect(() => {
    setAdmin(String(view.admin_session_minutes));
    setDispatcher(String(view.dispatcher_session_hours));
  }, [view.admin_session_minutes, view.dispatcher_session_hours, view.revision]);
  const a = boundedInteger(admin, b.admin_session_minutes.min, b.admin_session_minutes.max);
  const d = boundedInteger(dispatcher, b.dispatcher_session_hours.min, b.dispatcher_session_hours.max);
  const changed = a !== null && d !== null && (a !== view.admin_session_minutes || d !== view.dispatcher_session_hours);
  return (
    <div data-sessions-config>
      <Card title="Session lifetimes">
        <Rows rows={[
          ['Management Client', `${view.admin_session_minutes} minutes from sign-in`],
          ['Dispatcher', `${hoursText(view.dispatcher_session_hours)} from sign-in`],
          ['Signed in now', `${view.live_sessions.admin} Management Client, ${view.live_sessions.dispatcher} Dispatcher session(s)`],
          ['Last changed', `${when(view.updated_at)}${view.updated_by ? ` by ${view.updated_by}` : ''} (revision ${view.revision})`],
        ]} />
        <p className="mt-4 text-xs text-gray-400">
          One sign-in opens both: the Management Client session and the Dispatcher's own session. Each ends at its own time, counted from sign-in;
          activity never extends it. When the Management Client session ends the Dispatcher keeps running; Sign out (in either) ends both. Radio
          credentials and API keys are not sessions and are not affected.
        </p>
      </Card>
      <Card title="Change">
        <form onSubmit={(e) => { e.preventDefault(); if (changed && a !== null && d !== null) onSave(a, d); }} className="space-y-3 max-w-xl">
          <label className="block text-sm">
            <span className="text-gray-300">Management Client session (minutes, {b.admin_session_minutes.min}-{b.admin_session_minutes.max}; default {b.admin_session_minutes.default})</span>
            <input name="admin-session-minutes" type="number" inputMode="numeric" step={1} min={b.admin_session_minutes.min} max={b.admin_session_minutes.max}
              value={admin} onChange={(e) => setAdmin(e.target.value)} disabled={busy}
              className="mt-1 w-40 bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-white" />
            {a === null && <span role="alert" className="block text-xs text-red-300">A whole number of minutes from {b.admin_session_minutes.min} to {b.admin_session_minutes.max}.</span>}
          </label>
          <label className="block text-sm">
            <span className="text-gray-300">Dispatcher session (hours, {b.dispatcher_session_hours.min}-{b.dispatcher_session_hours.max}; default {b.dispatcher_session_hours.default})</span>
            <input name="dispatcher-session-hours" type="number" inputMode="numeric" step={1} min={b.dispatcher_session_hours.min} max={b.dispatcher_session_hours.max}
              value={dispatcher} onChange={(e) => setDispatcher(e.target.value)} disabled={busy}
              className="mt-1 w-40 bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-white" />
            {d === null && <span role="alert" className="block text-xs text-red-300">A whole number of hours from {b.dispatcher_session_hours.min} to {b.dispatcher_session_hours.max}.</span>}
          </label>
          <p className="text-xs text-gray-500">
            Applies at once to everyone who is signed in: a shorter lifetime ends sign-ins older than it (including possibly your own), a longer one
            extends sign-ins that are still active. Expired and signed-out sessions never come back. Needs a multi-factor sign-in; the change is audited.
          </p>
          <button type="submit" disabled={busy || !changed}
            className="px-3 py-1.5 rounded text-sm font-medium disabled:opacity-50 bg-blue-600 hover:bg-blue-500 text-white">
            Save session lifetimes
          </button>
        </form>
      </Card>
    </div>
  );
}

export function SessionsPanel() {
  const [view, setView] = useState<SessionPolicyView | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const load = useCallback(async () => {
    try {
      setView(await getSessionPolicy());
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : String(err) });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!view) return message ? <div role="status" className="rounded-lg p-3 text-sm border bg-red-900/30 border-red-700 text-red-200">{message.text}</div> : null;
  const save = async (adminMinutes: number, dispatcherHours: number) => {
    if (!window.confirm(`Change the session lifetimes for everyone?\n\n${changeSummary(view, adminMinutes, dispatcherHours)}`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const r = await saveSessionPolicy(adminMinutes, dispatcherHours, view.revision);
      setMessage({ ok: true, text: `Saved. ${r.adjusted_sessions?.admin ?? 0} Management Client and ${r.adjusted_sessions?.dispatcher ?? 0} Dispatcher session(s) now end at sign-in + the new lifetime.` });
      setView(r);
    } catch (err) {
      const issues = err instanceof ConfigurationError && err.issues.length ? `: ${err.issues.map((i) => i.message).join('; ')}` : '';
      const conflict = err instanceof ConfigurationError && err.code === 'POLICY_CHANGED' ? ' The current values are shown again.' : '';
      setMessage({ ok: false, text: (err instanceof Error ? err.message : String(err)) + issues + conflict });
      await load();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {message && (
        <div role="status" className={`mb-6 rounded-lg p-3 text-sm border ${message.ok ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-red-900/30 border-red-700 text-red-200'}`}>{message.text}</div>
      )}
      <SessionsView view={view} busy={busy} onSave={save} />
    </>
  );
}
